<template>
    <div class="marketing-task" :style="customStyle">
        <div class="content" :style="backgroundStyle">
            <section class="content-left">
                <img :src="taskIconImg" class="task-thumbnail" />
            </section>
            <section class="content-center">
                <div class="header" :style="{ color: taskTitleColor }">
                    <div class="task-title">
                        {{ taskTitle || '--' }}
                    </div>
                    <div class="task-progress">
                        {{
                            `${taskProgressNumerator}/${taskProgressDenominator}` ||
                            '0/1'
                        }}
                    </div>
                </div>
                <div class="description">
                    <div
                        :style="{ color: taskDescColor }"
                        class="task-description"
                    >
                        {{ taskDesc || '--' }}
                    </div>
                    <div :style="{ color: awardDescColor }">
                        {{ awardDesc || '--' }}
                    </div>
                </div>
            </section>
            <section class="content-right">
                <div
                    class="task-action"
                    :class="{ 'is-disabled': isTaskFinalState || isLoading }"
                    :style="btnStyle"
                    @click="onCardBtnClick"
                >
                    <span v-if="isLoading" :class="{ loading: isLoading }" />
                    {{ btnText }}
                </div>
            </section>
        </div>
        <nio-dialog
            :visible="isShowReceiveRewardDialog"
            :title="`${receiveRewardsDialog.title}`"
            :content="rewardDlgDescText"
            :cancelText="'关闭弹窗'"
            :confirmText="rewardDlgRightBtnText"
            :showConfirm="showDialogConfirm"
            @cancel="onRewardDlgClose"
            @confirm="onRewardDlgRightBtnClick"
        >
            <template slot="content" class="task-dialog">
                <img
                    v-if="rewardDlgAwardImg"
                    :src="rewardDlgAwardImg"
                    class="task-dialog-img"
                />
                <div class="task-dialog-text">{{ rewardDlgDescText }}</div>
            </template>
        </nio-dialog>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import {
    isInNioCNApp,
    redirectToLoginViewBridge,
    getLoginInfoBridge,
    redirectToViewBridge,
    copyBridge,
    handleBridgeCallback
} from '@/util/platform/universal'; // @/util/jsbridge/index
import { getUrlV2 } from '@/util/dmp';
import { getDomVisibleSettingCompatible } from '@/util/dom';
import { NIO_SCHEMA, DOWNLOAD_APP_URL } from '@/config/enums';
import NioDialog from '@/marketing_components/common/nio-dialog'; // @/components/common/nio-dialog
import trackExposeMixin from '../mixin/track-expose';

const mixin = {
    methods: {
        ...mapActions('marketing/', [
            'getTaskDetail',
            'checkTaskParticipation',
            'receiveTaskRewards'
        ])
    }
};

export default {
    name: 'marketing-task',
    components: { NioDialog },
    mixins: [mixin, trackExposeMixin],
    props: {
        customStyle: {
            type: String,
            default: 'margin-top: 0; margin-bottom:0'
        },
        receiveRewardsDialog: {
            type: Object,
            default: () => ({
                title: '领取完成！',
                desc: '任务完成，获得[reward_name]*[reward_number]',
                failedToast: '网络异常，请稍后重试！'
            })
        },
        taskParam: {
            type: Object,
            default: () => ({ taskId: '', taskAlias: '' })
        },
        backgroundSetting: {
            type: Object,
            default: () => ({
                backgroundType: 'color',
                background: '#fff'
            })
        },
        taskIconImg: String,
        goFinishBtnImg: String,
        getAwardBtnImg: String,
        receivedBtnImg: String,
        overdueBtnImg: String,
        taskTitle: String,
        taskTitleColor: String,
        taskDesc: String,
        taskDescColor: String,
        awardDesc: String,
        awardDescColor: String,
        trackSelectAction: Function,
        goFinishTargetUrl: {
            type: String,
            default: ''
        },
        receivedTargetUrl: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            isShowReceiveRewardDialog: false,
            isInApp: false,
            isLogin: false,
            isLoading: false,
            loginInfo: {},
            taskDetail: {},
            cdKeyList: []
        };
    },
    computed: {
        backgroundStyle() {
            const { backgroundType, background } = this.backgroundSetting;
            if (backgroundType === 'img') {
                return this.imgBgStyle(background);
            }
            if (backgroundType === 'color') {
                return { background };
            }
            return {};
        },
        btnStyle() {
            const { business_status } = this.taskDetail;
            let bg = {};
            let border = {};

            switch (business_status) {
                case 1:
                    bg = this.imgBgStyle(this.goFinishBtnImg);
                    border = this.btnBorderStyle(this.goFinishBtnImg);
                    return [bg, border];
                case 2:
                    bg = this.imgBgStyle(this.getAwardBtnImg);
                    border = this.btnBorderStyle(this.getAwardBtnImg);
                    return [bg, border];
                case 3:
                    bg = this.imgBgStyle(this.receivedBtnImg);
                    border = this.btnBorderStyle(this.receivedBtnImg);
                    return [bg, border];
                case 4:
                    bg = this.imgBgStyle(this.overdueBtnImg);
                    border = this.btnBorderStyle(this.overdueBtnImg);
                    return [bg, border];
                default:
                    return {};
            }
        },
        imgBgStyle() {
            return url => {
                if (url) {
                    return {
                        background: `center / cover no-repeat url(${url})`
                    };
                }
                return { background: '#fff' };
            };
        },
        btnBorderStyle() {
            return url => {
                if (!url) {
                    return { borderWidth: `${100 / 375}vw` };
                }
                return { borderWidth: '0' };
            };
        },
        isTaskFinalState() {
            if ([4].includes(this.taskDetail.business_status)) {
                // 已逾期禁止点击
                return true;
            }
            if (
                [3].includes(this.taskDetail.business_status) &&
                !this.receivedTargetUrl
            ) {
                // 已领取如果没有配跳转链接禁止点击
                return true;
            }
            return false;
        },
        btnText() {
            const { business_status, business_status_name } = this.taskDetail;
            const DEFAULT_BTN_TEXT = '去完成';

            switch (business_status) {
                case 1:
                    return this.goFinishBtnImg ? '' : business_status_name;
                case 2:
                    return this.getAwardBtnImg ? '' : business_status_name;
                case 3:
                    return this.receivedBtnImg ? '' : business_status_name;
                case 4:
                    return this.overdueBtnImg ? '' : business_status_name;
                default:
                    return DEFAULT_BTN_TEXT;
            }
        },
        taskProgressNumerator() {
            return this.taskDetail.event_completed_number || 0;
        },
        taskProgressDenominator() {
            const event = (this.taskDetail.event_list || [])[0] || {};
            if ([0, 2].includes(event.type)) {
                // 行为类型 == 单次/累次 || 行为类型 == 重复任务
                return (event.event_limit || {}).times || 1;
            } else {
                // 行为类型 == 首次任务 || default
                return 1;
            }
        },
        rewardDlgDescText() {
            const { name, single_quantity } =
                this.taskDetail.award_detail || {};
            const { desc } = this.receiveRewardsDialog;

            const presetVarMapping = {
                reward_name: name,
                reward_number: single_quantity,
                cd_key: this.cdKeyList.join('，')
            };

            const descReplaceVar = desc.replace(
                /(\[)(.*?)(\])/g,
                matchedVar => {
                    const keyInBracket = matchedVar.match(/\[(.*?)\]/g, '')[0];
                    const key = keyInBracket.slice(1, -1);

                    return presetVarMapping[key] || '';
                }
            );

            return descReplaceVar;
        },
        rewardDlgRightBtnText() {
            const { type } = this.taskDetail.award_detail || {};
            if ([1, 2].includes(type)) {
                // 积分 || 卡券
                return '查看奖励';
            } else if (type == 3) {
                // 兑换码
                return '复制';
            } else {
                // 盲盒暂时无法直接查看
                return '确定';
            }
        },
        showDialogConfirm() {
            const { type } = this.taskDetail.award_detail || {};
            if ([1, 2, 3].includes(type)) {
                // 积分 || 卡券
                return true;
            }
            return false;
        },
        rewardDlgAwardImg() {
            const { pic_url, type } = this.taskDetail.award_detail || {};
            if ([1, 2, 4].includes(type)) {
                return pic_url || ''; // 积分 || 卡券 || 盲盒
            }
            return '';
        }
    },
    methods: {
        async init() {
            this.isLoading = true;
            this.setIsInApp();
            await this.setLoginInfo();
            this.fetchTaskDetail().then(() => {
                this.isLoading = false;
            });
        },
        setIsInApp() {
            this.isInApp = isInNioCNApp();
        },
        async setLoginInfo() {
            if (this.isInApp) {
                const { data } = await getLoginInfoBridge();
                this.loginInfo = data;
                this.isLogin = !!data.access_token;
            } else {
                // @TODO: h5支持登录后需要修改
                this.loginInfo = {};
                this.isLogin = false;
            }
        },
        fetchTaskDetail() {
            const params = {
                task_alias: this.taskParam.taskAlias,
                mobile: this.loginInfo.mobile
            };
            return this.getTaskDetail(params)
                .then(res => {
                    this.taskDetail = res.data;
                })
                .catch(e => {
                    this.$message(`任务获取失败，请重试！${e.message || e}`);
                });
        },
        async onCardBtnClick() {
            await this.setLoginInfo();

            // 任务状态流转结束时 禁止点击
            if (this.isTaskFinalState) {
                await this.trackSelectComponent();
                return;
            }

            // 登录校验
            if (!this.isLogin) {
                await this.trackSelectComponent();
                if (this.isInApp) {
                    redirectToLoginViewBridge();
                    return;
                } else {
                    // 唤起app & 打开当前页 @TODO: H5支持登录后需要check逻辑
                    const url = getUrlV2(window.location.href, {
                        wv: 'lg',
                        load_js_bridge: true
                    });

                    this.callPage(
                        `nio://web/view?url=${encodeURIComponent(url)}`
                    );
                    return;
                }
            }

            // 参与校验（当前用户是否属于某人群包）
            const { data: isPass = false } = await this.checkTaskParticipation({
                mobile: this.loginInfo.mobile,
                task_alias: this.taskParam.taskAlias
            });
            if (!isPass) {
                this.$toast('当前用户无法参与本任务');
                await this.trackSelectComponent();
                return;
            }

            const { business_status, event_list = [] } = this.taskDetail;
            await this.trackSelectComponent(this.getClickUrl());
            switch (business_status) {
                case 1: //去完成
                    if (event_list.length) {
                        this.jumpToTargetPage(
                            this.goFinishTargetUrl ||
                                event_list[0].target_page_url,
                            true
                        );
                    }
                    break;
                case 2: // 领取奖励
                    this.receiveRewards();
                    break;
                case 3: //已完成
                    this.jumpToTargetPage(this.receivedTargetUrl, true);
                    break;
                case 4: //已逾期
                default:
                    break;
            }
        },
        jumpToTargetPage(href, noToast) {
            if (!href) {
                !noToast && this.$toast('目标链接不存在');
                return;
            }

            // 跳转：不校验url是否能跳转
            if (this.isInApp) {
                redirectToViewBridge(href);
            } else {
                if (href.startsWith('http')) {
                    this.callPage(`nio://web/view?url=${href}`); // 开一个新页面
                } else {
                    this.callPage(href);
                }
            }
        },
        callPage(href, timeout = 3000) {
            location.href = href;
            if (!href.startsWith('http') && !this.isInApp) {
                setTimeout(() => {
                    const { hidden, state } = getDomVisibleSettingCompatible();
                    if (document[state] !== hidden) {
                        // 如果页面还在前台（没有打开app）则会跳转到下载地址
                        window.location.href = DOWNLOAD_APP_URL;
                    }
                }, timeout);
            }
        },
        receiveRewards() {
            const { award_detail = {} } = this.taskDetail;
            const { issued_award = {}, type } = award_detail;

            // 领取奖励同时支持 兑换码领取、积分/卡券自动发放失败重发
            return this.receiveTaskRewards({
                biz_unique_id: issued_award.issue_code
            })
                .then(res => {
                    this.cdKeyList = res.data.prize_instance_list || [];
                    this.isShowReceiveRewardDialog = true;
                    this.fetchTaskDetail();
                })
                .catch(e => {
                    this.$message(this.receiveRewardsDialog.failedToast);
                });
        },
        onRewardDlgRightBtnClick() {
            const { type } = this.taskDetail.award_detail || {};
            switch (type) {
                case 1: // 积分
                    this.jumpToTargetPage(NIO_SCHEMA.MY_CREDIT);
                    break;
                case 2: // 卡券
                    this.jumpToTargetPage(NIO_SCHEMA.MY_CARD);
                    break;
                case 3: // 兑换码
                    this.copyToClipboard(this.cdKeyList.join('，'));
                    break;
                default:
                    this.onRewardDlgClose();
                    break;
            }
        },
        onRewardDlgClose() {
            this.isShowReceiveRewardDialog = false;
        },
        async copyToClipboard(content) {
            if (this.isInApp) {
                handleBridgeCallback(
                    await copyBridge(content),
                    () => this.$toast('复制成功！'),
                    () => this.$toast('复制失败！')
                );
            } else {
                this.$copyText(content).then(
                    () => {
                        this.$toast('复制成功！');
                    },
                    e => {
                        if (!e.text) {
                            this.$toast('复制失败！文本为空');
                        } else {
                            this.$toast('复制失败！');
                        }
                    }
                );
            }
        },
        async trackSelectComponent(url = '') {
            if (this.trackSelectAction) {
                await this.trackSelectAction({ click_url: url });
            }
        },
        getClickUrl() {
            // 任务状态
            const { business_status, event_list = [] } = this.taskDetail || {};
            if (business_status === 1) {
                //去完成
                return event_list.length
                    ? this.goFinishTargetUrl || event_list[0].target_page_url
                    : '';
            }
            if (business_status === 3) {
                // 已完成
                return this.receivedTargetUrl;
            }

            return '';
        }
    },
    mounted() {
        this.init();
    }
};
</script>
<style lang="less">
@import '~src/assets/css/variable.less';
.marketing-task {
    position: relative;

    .content {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        padding: 10 * @layout375 20 * @layout375;
        line-height: 22 * @layout375;

        &-left {
            width: 36 * @layout375;
            height: 36 * @layout375;
        }
        &-center {
            flex: 1;
            margin: 0 20 * @layout375 0 12 * @layout375;
        }
        &-right {
            min-width: 64 * @layout375;
            width: fit-content;
        }
    }

    .task-thumbnail,
    .task-thumbnail-error {
        width: 100%;
        height: 100%;
        display: block;
    }

    .task-thumbnail {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .header {
        display: flex;
        flex-wrap: wrap;
        justify-content: left;
        font-size: 16 * @layout375;
        color: @text_primary;
    }
    .task-title {
        margin-right: 8 * @layout375;
        min-width: fit-content;
    }
    .task-progress {
        min-width: fit-content;
    }

    .description {
        display: flex;
        flex-direction: column;
        font-size: 12 * @layout375;
        color: @text_primary;
    }

    .task-description {
        color: @text_secondary;
        font-size: 12 * @layout375;
    }

    .task-action {
        position: relative;
        width: 100%;
        min-height: 32 * @layout375;
        font-size: 14 * @layout375;
        padding: 5 * @layout375 10 * @layout375;
        border: 1 * @layout375 solid @border_primary;
        box-sizing: border-box;
        cursor: pointer;
        &.is-disabled {
            color: @text_disable;
            border-color: @border-divider_primary;
            cursor: not-allowed;
        }

        .loading {
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            margin: auto;
            height: 16 * @layout375;
            width: 16 * @layout375;
            border-width: 2 * @layout375;
            border-style: solid;
            border-radius: 50%;
            border-color: rgba(255, 255, 255, 0.8) rgba(58, 60, 68, 0.5)
                rgba(58, 60, 68, 0.5);
            animation: rotate-right 1s linear infinite normal;
        }
    }

    .task-dialog {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        &-img {
            width: 50 * @layout375;
            height: 50 * @layout375;
            margin-top: 8 * @layout375;
        }
        &-text {
            margin-top: 6 * @layout375;
        }
    }
}
@keyframes rotate-right {
    from {
        -webkit-transform: rotate(45deg);
        transform: rotate(45deg);
    }
    to {
        -webkit-transform: rotate(405deg);
        transform: rotate(405deg);
    }
}
</style>


// WEBPACK FOOTER //
// src/views/marketing/components/task.vue